import Close from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import { getCurrentTime } from "../utils/assistantUtils";
const cleanData = data => {
    if (Array.isArray(data)) {
        return data.map((value) => (value === "" ? null : cleanData(value)));
    }

    if (typeof data === "object" && data !== null) {
        return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, cleanData(value)]));
    }

    return data === "" ? null : data;
};

const AIA81Modal = ({setAia81Intent, originatingRequestId, requestId, handleModuleSubmit, handleStop, setMessages, applicationNumbers}) => {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [aia81Data, setAia81Data] = useState({
        application_nums: applicationNumbers || "",
        attorneyUpdateMethod: "powerOfAttorney", // powerOfAttorney, customerNumber, practitionerName
        customerNumber: "",
        practitioners: [
            {name: "", registrationNumber: ""},
            {name: "", registrationNumber: ""},
            {name: "", registrationNumber: ""},
            {name: "", registrationNumber: ""}
        ],
        correspondenceMethod: "sameAsAbove", // customerNumber, specificCustomerNumber
        specificCustomerNumber: "",
        applicantType: "applicant", // applicant, patentOwner
        patentOwnerDate: "",
        signatureRequired: true,
        signatoryInfo: {
            name: "",
            telephone: "",
            titleCompany: ""
        },
        formCount: "1",
        multipleFormsCheckbox: false
    });
    const [areSignatureDetailsValid, setAreSignatureDetailsValid] = useState(true);
    const [showSignatoryDetailsError, setShowSignatoryDetailsError] = useState(false);
    const [isRequiredField, setIsRequiredField] = useState(false);

    const scrollToLastFile = useRef(null);

    // Update application numbers when prop changes
    useEffect(() => {
        if (applicationNumbers) {
            setAia81Data(prev => ({
                ...prev,
                application_nums: applicationNumbers
            }));
        }
    }, [applicationNumbers]);

    useEffect(() => {
        setTimeout(() => {
            if (scrollToLastFile.current) {
                scrollToLastFile.current.scrollIntoView({behavior: "smooth"});
            }
        }, 1);
    }, [uploadedFiles]);

    useEffect(() => {
        setAreSignatureDetailsValid(!aia81Data.signatureRequired || (aia81Data.signatureRequired && !!aia81Data.signatoryInfo.name?.trim()));
    }, [aia81Data.signatureRequired, aia81Data.signatoryInfo.name]);

    const handleSubmit = async () => {
        // Validate required fields

        // Check validation based on attorney update method
        if (aia81Data.attorneyUpdateMethod === "customerNumber") {
            if (!aia81Data.customerNumber) {
                setIsRequiredField(true);
                return;
            }
        } else if (aia81Data.attorneyUpdateMethod === "practitionerName") {
            // Only check filled practitioner rows
            const filledPractitioners = aia81Data.practitioners.filter(p => p.name || p.registrationNumber);

            // Check if any partially filled practitioners
            const hasPartiallyFilledPractitioner = filledPractitioners.some(
                p => (!p.name && p.registrationNumber) || (p.name && !p.registrationNumber)
            );

            if (hasPartiallyFilledPractitioner) {
                setIsRequiredField(true);
                return;
            }

            // If practitioner method selected, require at least one practitioner
            if (filledPractitioners.length === 0) {
                setIsRequiredField(true);
                return;
            }
        }

        // Check correspondence method validation
        if (aia81Data.correspondenceMethod === "specificCustomerNumber") {
            if (!aia81Data.specificCustomerNumber) {
                setIsRequiredField(true);
                return;
            }
        }

        // Check patent owner date only if patent owner is selected
        if (aia81Data.applicantType === "patentOwner") {
            if (!aia81Data.patentOwnerDate) {
                setIsRequiredField(true);
                return;
            }
        }

        setIsRequiredField(false);
        // Don't override application_nums if already set from chatbot
        const finalApplicationNums = aia81Data.application_nums || applicationNumbers || "";
        const dataToSubmit = {
            ...aia81Data,
            application_nums: finalApplicationNums
        };

        const cleanedData = cleanData(dataToSubmit);
        const formattedString = JSON.stringify(cleanedData)
                                    .replace(/"([^"]+)":/g, "$1:")
                                    .replace(/null,\/\//g, "null,//");

        const message = `Please prepare AIA-81 Power of Attorney form with the following details:\n\n${formattedString}`;

        handleModuleSubmit({textInput: "AIA 81 Form Data", message, originatingRequestId, requestId, largeSlotData: formattedString});
        setAia81Intent(false);
    };

    const handleInputChange = (e) => {
        const {name, value, checked} = e.target;
        if (name === "signatureRequired" || name === "multipleFormsCheckbox") {
            setAia81Data((prev) => ({
                ...prev,
                [name]: checked
            }));

            if (name === "signatureRequired") {
                setShowSignatoryDetailsError(false);
            }
        } else {
            setAia81Data((prev) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSignatoryChange = (field, value) => {
        setAia81Data((prev) => ({
            ...prev,
            signatoryInfo: {
                ...prev.signatoryInfo,
                [field]: value
            }
        }));

        if (field === "name") {
            setShowSignatoryDetailsError(true);
        }
    };

    const handlePractitionerChange = (index, field, value) => {
        setAia81Data((prev) => ({
            ...prev,
            practitioners: prev.practitioners.map((practitioner, i) =>
                i === index ? {...practitioner, [field]: value} : practitioner
            )
        }));
    };

    const handleReset = () => {
        setAia81Data({
            application_nums: applicationNumbers || "", // Preserve application numbers from chatbot
            attorneyUpdateMethod: "powerOfAttorney",
            customerNumber: "",
            practitioners: [
                {name: "", registrationNumber: ""},
                {name: "", registrationNumber: ""},
                {name: "", registrationNumber: ""},
                {name: "", registrationNumber: ""}
            ],
            correspondenceMethod: "sameAsAbove",
            specificCustomerNumber: "",
            applicantType: "applicant",
            patentOwnerDate: "",
            signatureRequired: true,
            signatoryInfo: {
                name: "",
                telephone: "",
                titleCompany: ""
            },
            formCount: "",
            multipleFormsCheckbox: false
        });
        setUploadedFiles([]);
        setIsRequiredField(false);
        setShowSignatoryDetailsError(false);
    };

    return (
        <Box
            sx={{
                width: "100%",
                height: "92vh",
                background: "#FEFEFE",
                display: "flex",
                flexDirection: "column",
                padding: "1.5rem",
                gap: "1rem"
            }}
        >
            <Box sx={{display: "flex", justifyContent: "end"}}>
                <Button
                    onClick={() => {
                        setAia81Intent(false);
                        handleStop();
                        const time = getCurrentTime();
                        setMessages((prev) => [...prev, {from: "assistant", content: "Request cancelled.", time }]);
                    }}
                    sx={{
                        width: "fit-content",
                        ":hover": {
                            background: "transparent"
                        }
                    }}
                >
                    <Close/>
                </Button>
            </Box>
            <Box>
                <Typography fontSize={"1.5rem"} fontWeight="" color={"black"} sx={{mb: 2, mr: 1}}>PATENT- POWER OF ATTORNEY Patent Number OR REVOCATION OF POWER OF ATTORNEY WITH A NEW POWER OF ATTORNEY AND CHANGE OF CORRESPONDENCE ADDRESS</Typography>
                {/* <Typography fontSize={"1rem"} color={"black"} sx={{ mt: 1 }}>
          Please fill the below details to generate Power of Attorney form
        </Typography> */}
            </Box>
            <Box sx={{height: "60vh", overflowY: "scroll", padding: "1rem"}}>
                {/* Application Numbers */}
                <Box sx={{mb: 3}}>
                    <Typography fontSize={"1.1rem"} fontWeight="600" sx={{mb: 2}}>Please choose how you want to update the Applicant Address:</Typography>
                    <FormControl component="fieldset">
                        <RadioGroup name="attorneyUpdateMethod" value={aia81Data.attorneyUpdateMethod} onChange={handleInputChange}>
                            <FormControlLabel value="powerOfAttorney" control={<Radio sx={{"&.Mui-checked": {color: "#005CB9"}}}/>} label="A Power of Attorney is submitted herewith."/>
                            <FormControlLabel
                                value="customerNumber"
                                control={<Radio sx={{"&.Mui-checked": {color: "#005CB9"}}}/>}
                                label="I hereby appoint Practitioner(s) associated with the Customer Number identified in the box at right as my/our attorney(s) or agent(s) with respect to the patent identified above, and to transact all business in the United States Patent and Trademark Office connected therewith:"
                            />
                            {aia81Data.attorneyUpdateMethod === "customerNumber" && (
                                <Box sx={{my: 2, ml: 4}}>
                                    <TextField
                                        label="Customer Number"
                                        name="customerNumber"
                                        error={isRequiredField && aia81Data.attorneyUpdateMethod === "customerNumber" && !aia81Data.customerNumber}
                                        value={aia81Data.customerNumber}
                                        onChange={handleInputChange}
                                        required={aia81Data.attorneyUpdateMethod === "customerNumber"}
                                        sx={{
                                            width: "300px",
                                            "& label.Mui-focused": {
                                                color: "#005CB9"
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#005CB9"
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                            <FormControlLabel
                                value="practitionerName"
                                control={<Radio sx={{"&.Mui-checked": {color: "#005CB9"}}}/>}
                                label="I hereby appoint Practitioner(s) named below as my/our attorney(s) or agent(s) with respect to the patent identified above, and to transact all business in the United States Patent and Trademark Office connected therewith:"
                            />
                        </RadioGroup>
                    </FormControl>
                    {aia81Data.attorneyUpdateMethod === "practitionerName" && (
                        <Box sx={{mt: 2, ml: 4}}>
                            <Typography fontSize="1rem" fontWeight="500" sx={{mb: 1}}>
                                Practitioner Information (Maximum 4 practitioners):
                            </Typography>
                            <TableContainer component={Paper} sx={{maxWidth: "800px"}}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Practitioner Name</TableCell>
                                            <TableCell>Registration Number</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {aia81Data.practitioners.map((practitioner, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <TextField size="small" value={practitioner.name} error={isRequiredField && aia81Data.attorneyUpdateMethod === "practitionerName" && practitioner.name && !practitioner.registrationNumber} onChange={(e) => handlePractitionerChange(index, "name", e.target.value)} placeholder={`Practitioner ${index + 1} name`} required={aia81Data.attorneyUpdateMethod === "practitionerName" && practitioner.registrationNumber}/>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField size="small" value={practitioner.registrationNumber} error={isRequiredField && aia81Data.attorneyUpdateMethod === "practitionerName" && practitioner.registrationNumber && !practitioner.name} onChange={(e) => handlePractitionerChange(index, "registrationNumber", e.target.value)} placeholder={`Registration number`}/>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {isRequiredField && !aia81Data.practitioners.some(p => p.name && p.registrationNumber) && (<FormHelperText error sx={{mt: 1}}>At least one practitioner with both name and registration number is required</FormHelperText>)}
                        </Box>
                    )}
                </Box>
                {/* Correspondence Address */}
                <Box sx={{mb: 3}}>
                    <Typography fontSize={"1.1rem"} fontWeight="600" sx={{mb: 2}}>Please recognize or change the correspondence address for the above-identified patent to:</Typography>
                    <FormControl component="fieldset">
                        <RadioGroup name="correspondenceMethod" value={aia81Data.correspondenceMethod} onChange={handleInputChange}>
                            <FormControlLabel value="sameAsAbove" control={<Radio sx={{"&.Mui-checked": {color: "#005CB9"}}}/>} label="The address associated with the above-identified Customer Number."/>
                            <Box sx={{mt: 2}}>
                                <FormControlLabel value="specificCustomerNumber" control={<Radio sx={{"&.Mui-checked": {color: "#005CB9"}}}/>} label="The address associated with the Customer Number identified in the box at right:"/>
                                {aia81Data.correspondenceMethod === "specificCustomerNumber" && (
                                    <TextField
                                        label="Customer Number"
                                        name="specificCustomerNumber"
                                        error={isRequiredField && aia81Data.correspondenceMethod === "specificCustomerNumber" && !aia81Data.specificCustomerNumber}
                                        value={aia81Data.specificCustomerNumber}
                                        onChange={handleInputChange}
                                        // required={aia81Data.correspondenceMethod === "specificCustomerNumber"}
                                        sx={{
                                            width: "300px",
                                            "& label.Mui-focused": {
                                                color: "#005CB9"
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#005CB9"
                                                }
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </RadioGroup>
                    </FormControl>
                </Box>
                {/* Applicant Type */}
                <Box sx={{mb: 3}}>
                    <Typography fontSize={"1.1rem"} fontWeight="600" sx={{mb: 2}}>I am the:</Typography>
                    <FormControl component="fieldset">
                        <RadioGroup name="applicantType" value={aia81Data.applicantType} onChange={handleInputChange}>
                            <FormControlLabel value="applicant" control={<Radio sx={{"&.Mui-checked": {color: "#005CB9"}}}/>} label="Applicant"/>
                            <FormControlLabel value="patentOwner" control={<Radio sx={{"&.Mui-checked": {color: "#005CB9"}}}/>} label="Patent Owner"/>
                        </RadioGroup>
                    </FormControl>
                    {aia81Data.applicantType === "patentOwner" && (
                        <Box sx={{mt: 2, ml: 4}}>
                            <TextField
                                label="Statement under 37 CFR 3.73(c) (Form PTO/AIA/96) submitted herewith or filed on"
                                name="patentOwnerDate"
                                type="date"
                                error={isRequiredField && aia81Data.applicantType === "patentOwner" && !aia81Data.patentOwnerDate}
                                value={aia81Data.patentOwnerDate}
                                onChange={handleInputChange}
                                required={aia81Data.applicantType === "patentOwner"}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                sx={{
                                    width: "400px",
                                    "& label.Mui-focused": {
                                        color: "#005CB9"
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#005CB9"
                                        }
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
                {/* Signature Section */}
                <Box sx={{mb: 3}}>
                    <Typography fontSize={"1.1rem"} fontWeight="600" sx={{mb: 2}}>SIGNATURE of Applicant or Patent Owner</Typography>
                    <Typography fontSize={"0.9rem"} sx={{mb: 2, color: "gray"}}>The undersigned (whose title is supplied below) is authorized to act on behalf of the applicant or patent owner if the applicant or patent owner is a juristic entity (e.g., corporation, university, etc.).</Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="signatureRequired"
                                checked={aia81Data.signatureRequired}
                                onChange={handleInputChange}
                                sx={{
                                    "&.Mui-checked": {
                                        color: "#005CB9"
                                    }
                                }}
                            />
                        }
                        label="Would you like to add signatures?"
                    />
                    <Box sx={{mt: 2, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2}}>
                        <Box>
                            <TextField
                                label="Name"
                                value={aia81Data.signatoryInfo.name}
                                onChange={(e) => handleSignatoryChange("name", e.target.value)}
                                required={aia81Data.signatureRequired}
                                error={showSignatoryDetailsError && !areSignatureDetailsValid}
                                sx={{
                                    width: "100%",
                                    "& label.Mui-focused": {
                                        color: "#005CB9"
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#005CB9"
                                        }
                                    }
                                }}
                            />
                            {showSignatoryDetailsError && !areSignatureDetailsValid && <FormHelperText error>Name is required</FormHelperText>}
                        </Box>
                        <TextField
                            label="Telephone"
                            value={aia81Data.signatoryInfo.telephone}
                            onChange={(e) => handleSignatoryChange("telephone", e.target.value)}
                            sx={{
                                "& label.Mui-focused": {
                                    color: "#005CB9"
                                },
                                "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#005CB9"
                                    }
                                }
                            }}
                        />
                        <TextField
                            label="Title and Company"
                            value={aia81Data.signatoryInfo.titleCompany}
                            onChange={(e) => handleSignatoryChange("titleCompany", e.target.value)}
                            sx={{
                                gridColumn: "span 2",
                                "& label.Mui-focused": {
                                    color: "#005CB9"
                                },
                                "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#005CB9"
                                    }
                                }
                            }}
                        />
                    </Box>
                    {/* Note Section */}
                    <Box sx={{mt: 3, p: 2, backgroundColor: "#F9F9F9", borderRadius: 1}}>
                        <Typography fontSize={"0.9rem"} sx={{mb: 2, color: "#333", lineHeight: 1.5}}>
                            <strong>NOTE:</strong> Signatures of all the applicants or patent owners of the entire interest or their representative(s) are required. If more than one signature PATENT -POWER OF ATTORNEY Patent Number OR REVOCATION OF POWER OF ATTORNEY WITH A NEW POWER OF ATTORNEY AND CHANGE OF CORRESPONDENCE ADDRESS is required, submit multiple forms, check the box below, and identify the total number of forms submitted in the blank below.
                        </Typography>
                        <FormControlLabel
                            control={<Checkbox name="multipleFormsCheckbox" checked={aia81Data.multipleFormsCheckbox} onChange={handleInputChange}/>}
                            label={
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    <span>A total of</span>
                                    <TextField
                                        size="small"
                                        value={aia81Data.formCount}
                                        onChange={(e) => setAia81Data(prev => ({...prev, formCount: e.target.value}))}
                                        sx={{
                                            width: "80px",
                                            "& .MuiOutlinedInput-root": {
                                                height: "32px",
                                                "& fieldset": {
                                                    borderColor: "#CCC"
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#005CB9"
                                                }
                                            }
                                        }}
                                        placeholder="___"
                                    />
                                    <span>forms are submitted</span>
                                </Box>
                            }
                            sx={{alignItems: "center"}}
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2}}>
                <Button variant="outlined" onClick={handleReset}>Reset</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!areSignatureDetailsValid}>Submit</Button>
            </Box>
        </Box>
    );
};

AIA81Modal.propTypes = {
    setAia81Intent: PropTypes.func,
    originatingRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleModuleSubmit: PropTypes.func,
    handleStop: PropTypes.func,
    setMessages: PropTypes.func,
    applicationNumbers: PropTypes.string
};

export default AIA81Modal;